<script>
    import SvgIcon from "../../SVGIcon.svelte";

    export let isExpanded;
    export let isGridCard;
    export let url;
    export let ref;

    let youtubeVideoLink;
    let videoPlayerSize = { width: 0, height: 0 };
    $: youTubePlayerVisible = false;
    $: hasYoutubeVideo = false;

    let timeoutId;
    let buttonPosition = { top: 0, left: 0 };
    let playButtonRef;

    const VIDEO_PLAY_TIME_SECONDS = 15;

    const checkIfYoutubeVideo = ({ url, ref }) => {
        if (!url || !ref) return;

        const youtubeUrlRegex =
            /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?([^&]+)(?:&t=([^&]+))?/;

        // Correct the URL if the video ID has extra characters
        // YouTube video IDs are 11 characters long
        // example video: http://www.youtube.com/watch?v=FCHpRlwLtgIs&t=54s
        let videoIdStartIndex = url.indexOf("v=") + 2;
        if (url.length > videoIdStartIndex + 11) {
            let videoIdEndIndex = videoIdStartIndex + 11;
            let nextParamIndex = url.indexOf("&", videoIdStartIndex);
            if (nextParamIndex === -1 || nextParamIndex > videoIdEndIndex) {
                url = url.slice(0, videoIdEndIndex) + url.slice(nextParamIndex);
            }
        }

        // Check if the URL is a YouTube video
        const videoWithStartTime = url.match(youtubeUrlRegex);

        const image = ref.querySelector("img");
        if (image && videoWithStartTime) {
            hasYoutubeVideo = true;
            const urlObj = new URL(url);
            const urlParams = new URLSearchParams(urlObj.search);

            const videoID = urlParams.get("v");
            const startTime = urlParams.get("t")?.slice(0, -1);
            const endTime = startTime
                ? Number(startTime) + VIDEO_PLAY_TIME_SECONDS
                : VIDEO_PLAY_TIME_SECONDS;

            const startTimeParam = startTime ? `start=${startTime}` : ``;
            const endTimeParam = `end=${endTime}`;
            const timeParams = startTimeParam
                ? `${startTimeParam}&${endTimeParam}`
                : endTimeParam;

            youtubeVideoLink = `https://www.youtube-nocookie.com/embed/${videoID}?${timeParams}&controls=0&autoplay=1&disablekb=1&modestbranding=1&showinfo=0&rel=0`;
        }
    };

    // timeout awaits for expand animation to finish in order to
    // get the correct image size after possible scrollbar appearing
    const handlePlayButtonClick = () => {
        isExpanded = true;
        setTimeout(() => {
            videoPlayerSize = {
                width: ref.querySelector("img")?.clientWidth,
                height: ref.querySelector("img")?.clientHeight,
            };
            youTubePlayerVisible = true;
        }, 300);

        timeoutId = setTimeout(() => {
            youTubePlayerVisible = false;
        }, VIDEO_PLAY_TIME_SECONDS * 1000);
    };

    const handleStopButtonClick = () => {
        youTubePlayerVisible = false;
        clearTimeout(timeoutId);
    };

    // set up observer to wait for image load to finish loading in order to position play button
    $: if (ref) {
        if (!hasYoutubeVideo) checkIfYoutubeVideo({ url, ref });

        const image = ref.querySelector("img");
        const parent = image?.parentElement;
        if (image && parent && hasYoutubeVideo) {
            image.onload = () => {
                buttonPosition = {
                    top: image.offsetTop + 10 + "px",
                    left: image.clientWidth - 16 - 10 + "px",
                };
                parent.addEventListener("mouseenter", () => {
                    if (playButtonRef) playButtonRef.style.opacity = 1;
                    image.style.transition = "filter 0.3s";
                    image.style.filter = "brightness(80%)";
                });
                parent.addEventListener("mouseleave", () => {
                    if (playButtonRef) playButtonRef.style.opacity = 0;
                    image.style.filter = "brightness(100%)";
                });

                // Manually trigger the mouseenter event if the mouse is already over the element
                if (parent.matches(":hover")) {
                    let event = new Event("mouseenter");
                    parent.dispatchEvent(event);
                }
            };

            // Trigger button reposition on ref change
            buttonPosition = {
                top: image.offsetTop + 10 + "px",
                left: image.clientWidth - 16 - 10 + "px",
            };
        }
    }
</script>

<!-- increasing iframe size to cut out YouTube's content on top and bottom via parent overflow -->
<!-- content on top: video creator icon, title, dropdown menu -->
<!-- content on bottom: info about paid promotion, YouTube logo -->
{#if youTubePlayerVisible}
    <div
        class="youtube-iframe-wrapper group"
        style={`height: ${videoPlayerSize.height}px; width: ${videoPlayerSize.width}px;`}
    >
        <iframe
            title="YouTube video"
            class="youtube-iframe"
            width={videoPlayerSize.width}
            height={videoPlayerSize.height + 240}
            src={youtubeVideoLink}
            frameborder="0"
            allow="autoplay"
            referrerpolicy="strict-origin-when-cross-origin"
        >
        </iframe>
        <div
            class="stop-button"
            on:click={handleStopButtonClick}
            on:keydown={(event) => {
                if (event.key === "Enter") {
                    handleStopButtonClick();
                }
            }}
            tabindex="0"
            role="button"
        >
            <div class="stop-button-icon">
                <SvgIcon name="stop" />
            </div>
        </div>
    </div>
{:else}
    <slot />
    {#if hasYoutubeVideo && !isGridCard}
        <div
            on:click={handlePlayButtonClick}
            on:keydown={(event) => {
                if (event.key === "Enter") {
                    handlePlayButtonClick();
                }
            }}
            bind:this={playButtonRef}
            style={`
                top: ${buttonPosition.top};
                left: ${buttonPosition.left};
            `}
            tabindex="0"
            role="button"
            class="play-button"
        >
            <div class="play-button-icon">
                <SvgIcon name="play" />
            </div>
        </div>
    {/if}
{/if}

<style>
    .play-button {
        @apply absolute cursor-pointer rounded-full bg-transparent bg-opacity-50 p-1 text-white opacity-0 transition-opacity hover:bg-slate-800/50;
    }
    .play-button-icon {
        @apply flex translate-x-[1px];
    }
    .stop-button {
        @apply absolute left-0 top-0 grid h-full w-full place-items-center rounded-lg bg-black/20 opacity-0 transition-all group-hover:opacity-100;
    }
    .stop-button-icon {
        @apply flex rounded-full bg-slate-800/50 p-1 text-white;
    }
    .youtube-iframe-wrapper {
        @apply relative w-full overflow-hidden rounded-lg;
    }
    .youtube-iframe {
        @apply -mt-[120px] w-full rounded-lg;
    }
</style>
